import { format } from 'date-fns';
import moment from 'moment';
import { CSSProperties, Fragment } from 'react';
import { FETCH_RECEIPTS_V2 } from '~/actions';
import {
  Box,
  Center,
  Column,
  CustomSpinner,
  DataGrid,
  Icon,
  LineClamp,
  Row,
  ScrollArea,
  SortingArrow,
  Text,
} from '~/commonComponents';
import { EnforcePaymentHistoryTable } from '~/constants/ff.posthog';
import browserHistory from '~/history';
import { useLoading } from '~/hooks';
import { currencyFormat } from '~/hooks/currencyFormat';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { ArrowRightIcon } from '~/images/icons';
import { ReceiptType } from '~/models';
import { Colors } from '~/themes/colors';
import { SortFields } from './enums';
import { useAllTransactionReceiptV2 } from './hooks';
import { ReceiptStatus } from './Status';

interface IProps {
  filters: {
    transactionDate: string;
  };
}

const getColors = (type: ReceiptType) => {
  switch (type) {
    case ReceiptType.ListingFee:
      return Colors.jobs_listing_fee_background;
    case ReceiptType.OnsiteInterview:
      return Colors.jobs_interview_background;
    default:
      return Colors.jobs_shift_background;
  }
};

const getText = (type: ReceiptType) => {
  switch (type) {
    case ReceiptType.ListingFee:
      return 'LISTING FEE';
    case ReceiptType.OnsiteInterview:
      return 'INTERVIEW';
    default:
      return 'SHIFT';
  }
};

export const ReceiptTableV2 = ({ filters }: IProps) => {
  const { receipts, scrollContainerRef, sort, onFetchNextPage, onSortChange } =
    useAllTransactionReceiptV2({ filters });
  const { isLoading } = useLoading([FETCH_RECEIPTS_V2]);

  const isEnforcePaymentHistoryTable = useFeatureFlag(EnforcePaymentHistoryTable);

  return (
    <ScrollArea ref={scrollContainerRef} onEndReached={onFetchNextPage}>
      {isEnforcePaymentHistoryTable ? (
        <>
          <DataGrid
            columns={[
              {
                key: 'office',
                header: 'Office',
                render: (item) => (
                  <Text data-cy="office-name-column" paddingRight={8}>
                    <LineClamp lineClamp={2} text={item.officeName} />
                  </Text>
                ),
              },
              {
                key: 'type',
                header: 'Type',
                width: '140px',
                render: (item) => (
                  <Box
                    alignSelf="flex-start"
                    paddingVertical={2}
                    paddingHorizontal={10}
                    borderRadius={8}
                    backgroundColor={getColors(item.type)}
                  >
                    <Text color="neutral_500">{getText(item.type)}</Text>
                  </Box>
                ),
              },
              {
                key: 'transactionDate',
                header: (
                  <Row gap={8}>
                    <Text bold color={Colors.primary_500} fontSize={18}>
                      Transaction Date
                    </Text>
                    <SortingArrow
                      name={SortFields.TransactionDate}
                      sorting={{
                        direction: sort.order.toUpperCase() as 'ASC' | 'DESC',
                        field: sort.field,
                      }}
                      toggleOnly
                      onChange={(val) => onSortChange(val?.field as SortFields, val)}
                    />
                  </Row>
                ),
                width: '185px',
                render: (item) => (
                  <Column data-cy="payment-date-column">
                    <Text>{format(new Date(item.transactionDate), 'EEE, MMM dd, yyyy')}</Text>
                    <Text>{format(new Date(item.transactionDate), 'hh:mm a')}</Text>
                  </Column>
                ),
              },
              {
                key: 'shiftDate',
                header: (
                  <Row gap={8}>
                    <Text bold color={Colors.primary_500} fontSize={18}>
                      Posting Date
                    </Text>
                    <SortingArrow
                      name={SortFields.ShiftDate}
                      sorting={{
                        direction: sort.order.toUpperCase() as 'ASC' | 'DESC',
                        field: sort.field,
                      }}
                      onChange={(val) => onSortChange(val?.field as SortFields, val)}
                    />
                  </Row>
                ),
                width: '185px',
                render: (item) => (
                  <Text data-cy="shift-date-column">
                    {format(new Date(item.shiftDate), 'EEE, MMM dd, yyyy')}
                  </Text>
                ),
              },
              {
                key: 'total',
                header: 'Total',
                width: '120px',
                render: (item) => {
                  const isRefund = item.totalRefund > 0;

                  return (
                    <Text
                      bold
                      color={isRefund ? Colors.success_600 : Colors.neutral_600}
                      data-cy="payment-amount-column"
                      fontSize={15}
                    >
                      <span>{isRefund ? '-' : ''}$</span>
                      <span>
                        {isRefund
                          ? currencyFormat(item.totalRefund)
                          : currencyFormat(item.totalCharge)}
                      </span>
                    </Text>
                  );
                },
              },
              {
                key: 'status',
                header: 'Status',
                width: '120px',
                render: (item) => (
                  <Box data-cy="payment-status-column">
                    <ReceiptStatus status={item.status} />
                  </Box>
                ),
              },
              {
                key: 'action',
                header: '',
                width: '50px',
                align: 'center',
                render: () => <ArrowRightIcon />,
              },
            ]}
            data={receipts}
            dataCy={{
              header: 'payment-history-table',
              root: 'all-transactions-table',
              rowItem: 'payment-history-table-row',
            }}
            keyExtractor={(it) => `${it.officeId}.${it.shiftId}.${it.transactionDate}.${it.status}`}
            isStickyHeader
            onRowClick={(it) => {
              if (it.type === ReceiptType.ListingFee) {
                browserHistory.push(`/permanent-jobs/postings`);
                return;
              }

              browserHistory.push(`job/${it.shiftId}/details`, {
                job_id: it.shiftId,
                jobId: it.shiftId,
              });
            }}
          />
          {isLoading && (
            <Box marginTop={24}>
              <CustomSpinner isLoading size={16} />
            </Box>
          )}
        </>
      ) : (
        <table
          style={{ borderCollapse: 'collapse', width: '100%' }}
          data-cy="all-transactions-table"
        >
          <thead
            data-cy="payment-history-table-header"
            style={{
              position: 'sticky',
              top: 0,
              backgroundColor: Colors.white,
              zIndex: 1,
            }}
          >
            <tr>
              <th
                style={{
                  ...Styles.tableHeader,
                  width: 240,
                }}
              >
                Office
              </th>
              <th
                role="presentation"
                style={{
                  ...Styles.tableHeader,
                  cursor: 'pointer',
                }}
                onClick={() => onSortChange(SortFields.TransactionDate)}
              >
                <Row gap={4} justifyContent="center" width="100%">
                  <Column>
                    <span>Transaction</span>
                    <span>Date / Time</span>
                  </Column>
                  <SortingArrow
                    sorting={{
                      direction: sort.order.toUpperCase() as 'ASC' | 'DESC',
                      field: sort.field,
                    }}
                    name={SortFields.TransactionDate}
                  />
                </Row>
              </th>
              <th style={Styles.tableHeader}>Total Charge</th>
              <th style={Styles.tableHeader}>Total Refund</th>
              <th
                role="presentation"
                style={{
                  ...Styles.tableHeader,
                  cursor: 'pointer',
                }}
                onClick={() => onSortChange(SortFields.ShiftDate)}
              >
                <Row gap={4} justifyContent="center" width="100%">
                  <span>Shift Date</span>
                  <SortingArrow
                    sorting={{
                      direction: sort.order.toUpperCase() as 'ASC' | 'DESC',
                      field: sort.field,
                    }}
                    name={SortFields.ShiftDate}
                  />
                </Row>
              </th>
              <th style={Styles.tableHeader}>Status</th>
              <th style={Styles.tableHeader}>View Shift</th>
            </tr>
          </thead>
          <tbody>
            {receipts.map((it) => (
              <Fragment key={it.officeId}>
                <tr>
                  <td colSpan={7} height={10} />
                </tr>
                <tr
                  data-cy="payment-history-table-row"
                  role="presentation"
                  style={{
                    backgroundColor: Colors.neutral_50,
                    cursor: 'pointer',
                    height: 60,
                  }}
                  onClick={() => {
                    browserHistory.push(`job/${it.shiftId}/details`, {
                      job_id: it.shiftId,
                      jobId: it.shiftId,
                    });
                  }}
                >
                  <td
                    data-cy="office-name-column"
                    style={{
                      ...Styles.tableBody,
                      borderTopLeftRadius: 50,
                      borderBottomLeftRadius: 50,
                      overflow: 'hidden',
                      paddingInline: 16,
                    }}
                  >
                    <LineClamp lineClamp={2} text={it.officeName} />
                  </td>
                  <td data-cy="payment-date-column" style={Styles.tableBody}>
                    <Column>
                      <span>{moment(it.transactionDate).format('ddd, MMM, DD, YYYY')}</span>
                      <span>{moment(it.transactionDate).format('hh:mm')}</span>
                    </Column>
                  </td>
                  <td
                    data-cy="payment-amount-column"
                    style={{
                      ...Styles.tableBody,
                      fontWeight: 700,
                    }}
                  >
                    {it.totalCharge > 0 ? `$${it.totalCharge.toFixed(2)}` : '-'}
                  </td>
                  <td
                    data-cy="refund-amount-column"
                    style={{ ...Styles.tableBody, fontWeight: 700 }}
                  >
                    {it.totalRefund > 0 ? `$${it.totalRefund.toFixed(2)}` : '-'}
                  </td>
                  <td data-cy="shift-date-column" style={Styles.tableBody}>
                    {it.shiftDate}
                  </td>
                  <td data-cy="payment-status-column" style={Styles.tableBody}>
                    <ReceiptStatus status={it.status} />
                  </td>
                  <td
                    style={{
                      ...Styles.tableBody,
                      borderTopRightRadius: 50,
                      borderBottomRightRadius: 50,
                    }}
                  >
                    <Center>
                      <Icon name="arrow-right" />
                    </Center>
                  </td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </ScrollArea>
  );
};

const Styles: Record<string, CSSProperties> = {
  tableBody: {
    color: Colors.neutral_600,
    fontSize: 15,
    textAlign: 'center',
  },
  tableHeader: {
    borderBottom: `2px solid ${Colors.neutral_50}`,
    color: Colors.primary_500,
    fontSize: 16,
    fontWeight: 700,
    paddingBottom: 12,
    textAlign: 'center',
  },
};
